
import React, { useState } from 'react';
import { Navbar } from '@/components/Navbar';
import { useLanguage } from '@/context/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { usePayment } from '@/context/PaymentContext';
import ChatBox from '@/components/chat/ChatBox';
import PaymentHeader from '@/components/payment/PaymentHeader';
import StandardPaymentView from '@/components/payment/StandardPaymentView';
import AdvancedTransferForm from '@/components/payment/AdvancedTransferForm';
import PaymentSuccessView from '@/components/payment/PaymentSuccessView';
import StripeDirectPayment from '@/components/payment/StripeDirectPayment';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useForm } from "react-hook-form";
import { CardFormValues } from '@/components/payment/CardPaymentForm';
import { zodResolver } from "@hookform/resolvers/zod";
import { debitCardSchema } from '@/components/payment/CardPaymentForm';

const Payment = () => {
  const { language } = useLanguage();
  const { processAiStripePayment } = usePayment();
  const [showAdvancedTransfer, setShowAdvancedTransfer] = useState(false);
  const [recipientId, setRecipientId] = useState('');
  const [transferAmount, setTransferAmount] = useState('');
  const [paymentProcessed, setPaymentProcessed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const form = useForm<CardFormValues>({
    resolver: zodResolver(debitCardSchema),
    defaultValues: {
      cardNumber: "",
      cardholderName: "",
      expiryDate: "",
      cvv: "",
      saveCard: true,
    },
  });

  const translations = {
    en: {
      payment: "Payment",
      protected: "Protected",
      securityInfo: "Security Information",
      securityDesc: "All payments are end-to-end encrypted and processed securely over Bluetooth. Your payment details are never stored or shared with third parties.",
      deviceStorage: "Device-only Storage:",
      deviceDesc: "Your payment information never leaves your device. We use secure local storage to keep your data private.",
      advancedTransfer: "Advanced Transfer",
      recipient: "Recipient ID or Username",
      amount: "Amount to Transfer",
      transfer: "Transfer Now",
      aiAssisted: "AI-Assisted Card Transfer",
      aiAssistInfo: "Our AI system verifies your card securely before processing the payment through Stripe."
    },
    es: {
      payment: "Pago",
      protected: "Protegido",
      securityInfo: "Información de Seguridad",
      securityDesc: "Todos los pagos están cifrados de extremo a extremo y se procesan de forma segura a través de Bluetooth. Sus datos de pago nunca se almacenan ni se comparten con terceros.",
      deviceStorage: "Almacenamiento solo en dispositivo:",
      deviceDesc: "Su información de pago nunca sale de su dispositivo. Utilizamos almacenamiento local seguro para mantener sus datos privados.",
      advancedTransfer: "Transferencia Avanzada",
      recipient: "ID de Destinatario o Nombre de Usuario",
      amount: "Cantidad a Transferir",
      transfer: "Transferir Ahora",
      aiAssisted: "Transferencia de Tarjeta Asistida por IA",
      aiAssistInfo: "Nuestro sistema de IA verifica su tarjeta de forma segura antes de procesar el pago a través de Stripe."
    },
    fr: {
      payment: "Paiement",
      protected: "Protégé",
      securityInfo: "Informations de Sécurité",
      securityDesc: "Tous les paiements sont chiffrés de bout en bout et traités en toute sécurité via Bluetooth. Vos données de paiement ne sont jamais stockées ni partagées avec des tiers.",
      deviceStorage: "Stockage uniquement sur l'appareil:",
      deviceDesc: "Vos informations de paiement ne quittent jamais votre appareil. Nous utilisons un stockage local sécurisé pour garder vos données privées.",
      advancedTransfer: "Transfert Avancé",
      recipient: "ID du Destinataire ou Nom d'Utilisateur",
      amount: "Montant à Transférer",
      transfer: "Transférer Maintenant",
      aiAssisted: "Transfert de Carte Assisté par IA",
      aiAssistInfo: "Notre système d'IA vérifie votre carte en toute sécurité avant de traiter le paiement via Stripe."
    }
  };

  const tx = translations[language as keyof typeof translations];
  
  const handleAiVerified = (success: boolean) => {
    if (success) {
      toast.success("Card verified by AI", {
        description: "Your card has been verified and is ready for payment processing"
      });
    } else {
      toast.error("Card verification failed", {
        description: "Our AI system could not verify your card. Please check the details and try again."
      });
    }
  };
  
  const handleProcessStripePayment = async () => {
    if (!recipientId || !transferAmount || parseFloat(transferAmount) <= 0) {
      toast.error("Invalid payment details", {
        description: "Please enter a valid recipient and amount"
      });
      return;
    }
    
    const formValues = form.getValues();
    if (!form.formState.isValid) {
      form.trigger();
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const success = await processAiStripePayment(
        formValues,
        parseFloat(transferAmount),
        recipientId
      );
      
      if (success) {
        setPaymentProcessed(true);
        toast.success("Payment successful", {
          description: "Your payment has been processed through our AI-verified Stripe gateway"
        });
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Payment failed", {
        description: "An error occurred while processing your payment"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setShowAdvancedTransfer(false);
    setPaymentProcessed(false);
    setRecipientId('');
    setTransferAmount('');
    form.reset();
  };
  
  return (
    <div className="min-h-screen bg-gray-50">
      <header className="pt-12 pb-6 px-6">
        <PaymentHeader 
          title={tx.payment}
          protectedText={tx.protected}
        />
      </header>
      
      <main className="px-6 pb-24 pt-4">
        {!showAdvancedTransfer ? (
          <div className="max-w-md mx-auto">
            <Tabs defaultValue="standard" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="standard">Standard</TabsTrigger>
                <TabsTrigger value="stripe">Stripe Payment</TabsTrigger>
              </TabsList>
              <TabsContent value="standard">
                <StandardPaymentView 
                  advancedTransferText={tx.advancedTransfer}
                  onAdvancedTransferClick={() => setShowAdvancedTransfer(true)}
                />
              </TabsContent>
              <TabsContent value="stripe">
                <StripeDirectPayment />
              </TabsContent>
            </Tabs>
          </div>
        ) : (
          <div className="max-w-md mx-auto">
            <Card>
              <CardContent className="p-0">
                {!paymentProcessed ? (
                  <AdvancedTransferForm
                    title={tx.aiAssisted}
                    description={tx.aiAssistInfo}
                    recipientLabel={tx.recipient}
                    amountLabel={tx.amount}
                    onBack={() => setShowAdvancedTransfer(false)}
                    onSubmit={handleProcessStripePayment}
                    form={form}
                    recipientId={recipientId}
                    setRecipientId={setRecipientId}
                    transferAmount={transferAmount}
                    setTransferAmount={setTransferAmount}
                    isProcessing={isProcessing}
                    buttonText={`Pay $${parseFloat(transferAmount || '0').toFixed(2)} with AI-Verified Stripe`}
                  />
                ) : (
                  <PaymentSuccessView onReset={handleReset} />
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>
      
      <ChatBox />
      <Navbar />
    </div>
  );
};

export default Payment;
